import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { AuthContext } from '../App';

// High-five pixel art for hero banner
const HF_PX = 4;
const HF_COLORS = {
  a: '#D6E9AA', A: '#e4f2c1', // sage (left hand)
  k: '#ff6b9d', K: '#ff9dbf', // pink (right hand)
  y: '#ffd700',
  d: '#2a2a2e',
  s: '#adadb8',
};

const HIGH_FIVE_ROWS = [
  '..y..............y....',
  '...aAa........kKk.....',
  '...aAAa......kKKk.....',
  '...aAAa......kKKk.....',
  '...aAAa......kKKk.....',
  '..aAAAAa....kKKKKk....',
  '..aAAAAa....kKKKKk....',
  '..aAAAAa....kKKKKk....',
  '...aAAAAa..kKKKKk.....',
  '...aAAAAAkkKKKKk......',
  '....aAAAAkKKKKk.......',
  '.....aAAAkKKKk........',
  '......aAAkKKk.........',
  '.....aAAAkKKKk........',
  '....aAAAAkKKKKk.......',
  '...aAAAAa.kKKKKk......',
  '..aAAAAa...kKKKKk.....',
  '..aAAa.......kKKk.....',
  '...y..............y...',
];

function HighFiveSprite() {
  const shadows = [];
  const parsed = HIGH_FIVE_ROWS.map(r => r.split(''));
  for (let y = 0; y < parsed.length; y++) {
    for (let x = 0; x < parsed[y].length; x++) {
      const ch = parsed[y][x];
      if (ch !== '.' && HF_COLORS[ch]) {
        shadows.push(`${x * HF_PX}px ${y * HF_PX}px 0 ${HF_COLORS[ch]}`);
      }
    }
  }
  const w = Math.max(...parsed.map(r => r.length));
  return (
    <div style={{ width: w * HF_PX, height: parsed.length * HF_PX, position: 'relative', imageRendering: 'pixelated' }}>
      <div style={{ position: 'absolute', width: HF_PX, height: HF_PX, boxShadow: shadows.join(',') }} />
    </div>
  );
}

// Reuse pixel art sprites from CategoriesPage
const PX = 3;
const COLORS = {
  p: '#9147ff', P: '#e4f2c1', r: '#eb0400', R: '#ff6b6b', g: '#4caf50', G: '#81c784',
  b: '#2196f3', B: '#64b5f6', o: '#ff9800', O: '#ffcc80', y: '#ffd700', Y: '#fff176',
  k: '#ff6b9d', K: '#ff9dbf', c: '#00bcd4', C: '#4dd0e1', w: '#efeff1', d: '#2a2a2e',
  m: '#ce93d8', M: '#e1bee7', f: '#ff5722', F: '#ff8a65', n: '#ffa726', N: '#ffcc80',
  l: '#66bb6a', L: '#a5d6a7', s: '#adadb8', x: '#f44336', X: '#ef9a9a',
};

function MiniSprite({ rows, size = PX }) {
  const shadows = [];
  const parsed = rows.map(r => r.split(''));
  for (let y = 0; y < parsed.length; y++) {
    for (let x = 0; x < parsed[y].length; x++) {
      const ch = parsed[y][x];
      if (ch !== '.' && COLORS[ch]) {
        shadows.push(`${x * size}px ${y * size}px 0 ${COLORS[ch]}`);
      }
    }
  }
  const w = Math.max(...parsed.map(r => r.length));
  return (
    <div style={{ width: w * size, height: parsed.length * size, position: 'relative', flexShrink: 0 }}>
      <div style={{ position: 'absolute', width: size, height: size, boxShadow: shadows.join(',') }} />
    </div>
  );
}

const SPRITES = {
  gaming: [
    '............',
    '..dddddddd..',
    '.dppppppppd.',
    '.dpPPPPPPpd.',
    '.dpPPPPPPpd.',
    '.dppppppppd.',
    '.dddddddddd.',
    '..d..dd..d..',
    '.ddd.dd.ddd.',
    '.dPd.dd.dPd.',
    '.ddd.dd.ddd.',
    '............',
  ],
  hangout: [
    '............',
    '..kkkkkkkk..',
    '.kKKKKKKKKk.',
    '.kKddKddKKk.',
    '.kKKKKKKKKk.',
    '.kKKdddKKKk.',
    '.kKKKKKKKKk.',
    '..kkkkkkkk..',
    '....kk......',
    '...kk.......',
    '............',
    '............',
  ],
  music: [
    '........pp..',
    '.......pppp.',
    '.......p..p.',
    '.......p..p.',
    '.......p..p.',
    '.......p..p.',
    '..pp...p..p.',
    '.pPPp.pp.pp.',
    'pPPPPppPPPp.',
    'pPPPPppPPPp.',
    '.pPPp..pPp..',
    '..pp....pp..',
  ],
  art: [
    '...oooo.....',
    '..oOOOOo....',
    '.oOrrgbo....',
    'oOrrrgbo....',
    'oOOOOgbo....',
    'oOOOObbo....',
    '.oOOOoo.....',
    '..oooo......',
    '.....ss.....',
    '......ss....',
    '.......ss...',
    '........s...',
  ],
  craft: [
    '............',
    '..ss........',
    '.s..s.......',
    's....s.oooo.',
    '.s..s.oOOOo.',
    '..ss.oOOOOo.',
    '.s..soOOOOo.',
    's....oOOOOo.',
    '.s..s.oOOOo.',
    '..ss...oooo.',
    '............',
    '............',
  ],
  coding: [
    '............',
    '.dddddddddd.',
    '.dggggggggd.',
    '.dg.gg...gd.',
    '.dg..g.g.gd.',
    '.dg.g..g.gd.',
    '.dg....g.gd.',
    '.dgGGGGGGd.',
    '.dggggggggd.',
    '.dddddddddd.',
    '............',
    '............',
  ],
  esports: [
    '....yyyy....',
    '...yYYYy....',
    '..yYYYYYy...',
    '.xxYYYYYxx..',
    'xxxyYYYyxxx.',
    'xXx.yYy.xXx',
    'xXx..y..xXx',
    '.xx.....xx..',
    '..xxxxxxx...',
    '..xXXXXXx...',
    '..xxxxxxx...',
    '............',
  ],
  fitness: [
    '............',
    '....ww......',
    '...wdw......',
    '..llwll.....',
    '.l.lwl.l....',
    '...lwl......',
    '..ll.ll.....',
    '.ll...ll....',
    '.l.....l....',
    '............',
    '............',
    '............',
  ],
  education: [
    '............',
    '..bbbbbbbb..',
    '.bBBBBBBBBb.',
    '.bBssBsBBBb.',
    '.bBBBBBBBBb.',
    '.bBsBBssBBb.',
    '.bBBBBBBBBb.',
    '.bBssBsBBBb.',
    '.bBBBBBBBBb.',
    '..bbbbbbbb..',
    '....b..b....',
    '............',
  ],
  focus: [
    '............',
    '...kkkkkk...',
    '..kKKKKKKk..',
    '.kKkkkkkKKk.',
    'kKkk..kkKKk.',
    'kKk.kk.kKKk.',
    'kKk.kk.kKKk.',
    'kKkk..kkKKk.',
    '.kKkkkkkKKk.',
    '..kKKKKKKk..',
    '...kkkkkk...',
    '............',
  ],
  anime: [
    '............',
    '....www.....',
    '...wdddw....',
    '..wdddddw...',
    '.wdYddYddw..',
    '.wddddddw...',
    '.wdd.d.dw...',
    '.wdddddw....',
    '..wwdww.....',
    '...www......',
    '..w...w.....',
    '............',
  ],
};

const CAT_COLORS = {
  All: '#efeff1', Gaming: '#9147ff', Hangout: '#ff6b9d', Music: '#e91e63',
  Art: '#ff9800', Craft: '#e89140', Coding: '#4caf50', Esports: '#f44336',
  Fitness: '#66bb6a',
  Education: '#42a5f5', Focus: '#ff6b9d', Anime: '#e040fb',
};

const CATEGORIES = ['All', 'Hangout', 'Gaming', 'Esports', 'Anime', 'Music', 'Art', 'Craft', 'Coding', 'Education', 'Focus', 'Fitness'];

// All sprite for the "All" tab
const ALL_SPRITE = [
  '..wwwwww....',
  '.wssssssw...',
  '.ws.ss.sw...',
  '.wssssssw...',
  '.ws.ss.sw...',
  '.wssssssw...',
  '..wwwwww....',
  '............',
  '............',
  '............',
  '............',
  '............',
];

function ChannelCard({ ch, navigate, onTagClick, activeTag }) {
  return (
    <div className="channel-card" onClick={() => navigate(ch.is_live ? `/${ch.username}/watch` : `/${ch.username}`)}>
      <div className="channel-thumbnail">
        {ch.is_live && ch.mux_playback_id ? (
          <video
            src={`https://stream.mux.com/${ch.mux_playback_id}.m3u8`}
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span className="offline-icon" style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.8rem', color: '#2a2a2e' }}>[=]</span>
        )}
        {ch.is_live && <span className="live-badge">LIVE</span>}
      </div>
      <div className="channel-info">
        <div className="channel-name">{ch.display_name || ch.username}</div>
        <div className="channel-category">{ch.category}</div>
        {ch.live_tags?.length > 0 && (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }} onClick={e => e.stopPropagation()}>
            {ch.live_tags.map(t => (
              <button key={t} onClick={() => onTagClick?.(t)}
                style={{ padding: '2px 8px', borderRadius: '2px', fontSize: '0.7rem', fontWeight: 600, background: activeTag === t ? '#D6E9AA' : '#2a2a2e', color: activeTag === t ? '#1a1a1a' : '#adadb8', border: 'none', cursor: 'pointer', boxShadow: '1px 1px 0 rgba(0,0,0,0.2)' }}>
                {t}
              </button>
            ))}
          </div>
        )}
        <div style={{ fontSize: '0.8rem', color: '#adadb8', marginTop: '4px' }}>
          {ch.follower_count} followers
        </div>
      </div>
    </div>
  );
}

function RecommendCard({ ch, navigate }) {
  return (
    <div className="channel-card" onClick={() => navigate(`/${ch.username}`)} style={{ minWidth: 0 }}>
      <div className="channel-thumbnail">
        {ch.is_live ? (
          <span style={{ fontSize: '1.2rem' }}>🔴</span>
        ) : (
          <span className="offline-icon" style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.8rem', color: '#2a2a2e' }}>[=]</span>
        )}
        {ch.is_live && <span className="live-badge">LIVE</span>}
      </div>
      <div className="channel-info">
        <div className="channel-name">{ch.display_name || ch.username}</div>
        <div className="channel-category">{ch.category}</div>
        <div style={{ fontSize: '0.8rem', color: '#adadb8', marginTop: '4px' }}>
          {ch.follower_count} followers
        </div>
      </div>
    </div>
  );
}

export default function BrowsePage() {
  const { user } = useContext(AuthContext);
  const [category, setCategory] = useState('All');
  const [channels, setChannels] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag') || '';

  // Support ?category= from categories page links
  const urlCategory = searchParams.get('category') || '';
  useEffect(() => {
    if (urlCategory) {
      const match = CATEGORIES.find(c => c.toLowerCase() === urlCategory.toLowerCase());
      if (match) setCategory(match);
    }
  }, [urlCategory]);

  // Load featured + recommendations on mount
  useEffect(() => {
    api.featured().then(setFeatured).catch(() => {});
    if (user) api.recommendations().then(setRecommendations).catch(() => {});
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const cat = category === 'All' ? '' : category.toLowerCase();
    api.channels(cat, activeTag)
      .then(setChannels)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [category, activeTag]);

  const handleTagClick = (tag) => {
    if (activeTag === tag) {
      setSearchParams({});
    } else {
      setSearchParams({ tag });
      setCategory('All');
    }
  };

  const handleCategoryClick = (c) => {
    setCategory(c);
    setSearchParams({});
  };

  const showFeatured = category === 'All' && !activeTag && featured.length > 0;
  const showRecommendations = category === 'All' && !activeTag && recommendations.length > 0;
  const showHero = category === 'All' && !activeTag;

  return (
    <div className="page">
      {/* Brand hero */}
      {showHero && (
        <div className="hero-banner">
          <div className="hero-content">
            <div className="hero-sprite">
              <HighFiveSprite />
            </div>
            <div className="hero-text">
              <h1 className="hero-title">
                <span style={{ fontFamily: 'var(--font-pixel)', color: 'var(--accent)' }}>NINTY</span>
              </h1>
              <p className="hero-tagline">By streamers, for streamers.</p>
              <div className="hero-splits">
                <div className="hero-split">
                  <span className="hero-split-pct" style={{ color: 'var(--accent)' }}>90%</span>
                  <span className="hero-split-label">goes to streamers</span>
                </div>
                <div className="hero-split-divider" />
                <div className="hero-split">
                  <span className="hero-split-pct" style={{ color: '#ff6b9d' }}>5%</span>
                  <span className="hero-split-label">for charity</span>
                </div>
                <div className="hero-split-divider" />
                <div className="hero-split">
                  <span className="hero-split-pct" style={{ color: '#adadb8' }}>5%</span>
                  <span className="hero-split-label">keeps the lights on</span>
                </div>
              </div>
              {!user && (
                <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary" onClick={() => navigate('/signup')}>Start Streaming</button>
                  <button className="btn btn-outline" onClick={() => navigate('/login')}>Log In</button>
                </div>
              )}
            </div>
            <div className="hero-links">
              <button className="hero-link-btn hero-link-sponsors" onClick={() => navigate('/sponsors')}>
                <span className="hero-link-icon">
                  <MiniSprite rows={[
                    '...yyyy...',
                    '..yYYYYy..',
                    '.yYYYYYYy.',
                    'yYYddYYYy.',
                    'yYdddYYYy.',
                    'yYYddYYYy.',
                    '.yYYYYYYy.',
                    '..yYYYYy..',
                    '...yyyy...',
                  ]} size={3} />
                </span>
                <span className="hero-link-label">Sponsors</span>
              </button>
              <button className="hero-link-btn hero-link-partners" onClick={() => navigate('/partners')}>
                <span className="hero-link-icon">
                  <MiniSprite rows={[
                    '.kk...kk.',
                    'kKKk.kKKk',
                    'kKKKkKKKk',
                    'kKKKKKKKk',
                    '.kKKKKKk.',
                    '..kKKKk..',
                    '...kKk...',
                    '....k....',
                  ]} size={3} />
                </span>
                <span className="hero-link-label">Partners</span>
              </button>
              <button className="hero-link-btn hero-link-careers" onClick={() => navigate('/careers')}>
                <span className="hero-link-icon">
                  <MiniSprite rows={[
                    '....ww....',
                    '...wddw...',
                    '..wddddw..',
                    '.wwwwwwww.',
                    '.wDDDDDDw.',
                    '.wDaaaDDw.',
                    '.wDDDDDDw.',
                    '.wDaaaDDw.',
                    '.wDDDDDDw.',
                    '.wwwwwwww.',
                  ]} size={3} />
                </span>
                <span className="hero-link-label">Careers</span>
              </button>
              <button className="hero-link-btn hero-link-guidelines" onClick={() => navigate('/guidelines')}>
                <span className="hero-link-icon">
                  <MiniSprite rows={[
                    '..ssssss..',
                    '.sDDDDDDs.',
                    '.sD.DD.Ds.',
                    '.sDDDDDDs.',
                    '.sD.DDD.s.',
                    '.sDDDDDDs.',
                    '.sD.DD.Ds.',
                    '.sDDDDDDs.',
                    '..ssssss..',
                  ]} size={3} />
                </span>
                <span className="hero-link-label">Rules</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTag && (
        <div style={{ marginBottom: '16px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '2px', background: '#D6E9AA22', border: '1px solid #D6E9AA44', fontFamily: 'var(--font-pixel)', fontSize: '0.5rem', color: '#D6E9AA', fontWeight: 600, boxShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}>
            TAG: {activeTag}
            <button onClick={() => setSearchParams({})} style={{ background: 'none', border: 'none', color: '#D6E9AA', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-pixel)', fontSize: '0.5rem', lineHeight: 1 }}>X</button>
          </span>
        </div>
      )}

      {/* Featured section — only on "All" with no tag filter */}
      {showFeatured && (
        <div style={{ marginBottom: '32px' }}>
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.7rem', color: '#D6E9AA' }}>*</span>
            Featured
          </div>
          <div className="featured-row">
            {featured.map(ch => (
              <div key={ch.id} className="featured-card" onClick={() => navigate(`/${ch.username}/watch`)}>
                <div className="featured-thumbnail">
                  {ch.mux_playback_id ? (
                    <video
                      src={`https://stream.mux.com/${ch.mux_playback_id}.m3u8`}
                      muted
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: '#2a2a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontFamily: 'var(--font-pixel)', color: '#3a3a3e' }}>[=]</span>
                    </div>
                  )}
                  <span className="live-badge">LIVE</span>
                  {ch.viewer_count > 0 && (
                    <span style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px', borderRadius: '2px' }}>
                      {ch.viewer_count.toLocaleString()} viewers
                    </span>
                  )}
                </div>
                <div className="featured-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '2px', background: '#2a2a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', flexShrink: 0, overflow: 'hidden', boxShadow: '1px 1px 0 rgba(0,0,0,0.3)' }}>
                      {ch.avatar_url
                        ? <img src={ch.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : (ch.display_name || ch.username).charAt(0).toUpperCase()
                      }
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ch.display_name || ch.username}</div>
                      <div style={{ fontSize: '0.75rem', color: '#adadb8' }}>{ch.title}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#adadb8', marginTop: '6px', textTransform: 'capitalize' }}>{ch.category}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* You Might Like — only on "All", logged in, with recommendations */}
      {showRecommendations && (
        <div style={{ marginBottom: '32px' }}>
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontFamily: 'var(--font-pixel)', fontSize: '0.7rem', color: '#ff6b9d' }}>&lt;3</span>
            You Might Like
          </div>
          <div className="channel-grid">
            {recommendations.map(ch => (
              <RecommendCard key={ch.id} ch={ch} navigate={navigate} />
            ))}
          </div>
        </div>
      )}

      {/* Main channel grid */}
      <div className="section-title">
        {category === 'All' && !activeTag ? 'All Channels' : activeTag ? `Tag: ${activeTag}` : category}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#adadb8' }}>Loading...</div>
      ) : channels.length === 0 ? (
        <div className="offline-banner">
          <div style={{ fontFamily: 'var(--font-pixel)', fontSize: '1.2rem', marginBottom: '12px', color: '#2a2a2e' }}>[=]</div>
          <h2>{activeTag ? `No one live with tag "${activeTag}"` : 'No channels live right now'}</h2>
          <p style={{ marginTop: '8px' }}>Check back soon or sign up to start streaming!</p>
        </div>
      ) : (
        <div className="channel-grid">
          {channels.map(ch => (
            <ChannelCard key={ch.id} ch={ch} navigate={navigate} onTagClick={handleTagClick} activeTag={activeTag} />
          ))}
        </div>
      )}
    </div>
  );
}
